import React, { useState } from 'react';
import { Layers, Download, Info, DollarSign } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency, formatDate } from '../../../../../utils/formatting';

/**
 * Компонент для отображения детализации расходов по дням командировки
 */
const ExpenseBreakdown = ({ expenses, results }) => {
  const [showFullBreakdown, setShowFullBreakdown] = useState(false);
  
  if (!expenses || expenses.length === 0 || !results) {
    return null;
  }

  // Для отображения только первой недели, если не показана полная детализация
  const displayExpenses = showFullBreakdown ? expenses : expenses.slice(0, 7);

  // Расчет итоговых сумм
  const totals = {
    perDiem: expenses.reduce((sum, item) => sum + item.perDiem, 0),
    accommodation: expenses.reduce((sum, item) => sum + item.accommodation, 0),
    transport: expenses.reduce((sum, item) => sum + item.transport, 0),
    additional: expenses.reduce((sum, item) => sum + item.additional, 0),
    total: expenses.reduce((sum, item) => sum + item.total, 0)
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Layers className="w-5 h-5 mr-2 text-indigo-500" />
          Детализация расходов по дням
        </h3>

        {/* Кнопка переключения полной/краткой детализации */}
        {expenses.length > 7 && (
          <Button
            size="sm"
            onClick={() => setShowFullBreakdown(prev => !prev)}
            variant="outline"
            aria-expanded={showFullBreakdown}
          >
            {showFullBreakdown ? 'Показать краткую детализацию' : `Показать все ${expenses.length} дней`}
          </Button>
        )}
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>{showFullBreakdown 
            ? "Полная детализация расходов по всем дням командировки. Прокрутите таблицу вправо для просмотра всех данных." 
            : `Показаны первые ${displayExpenses.length} дней командировки. Нажмите "Показать все дни" для просмотра полной детализации.`
          }</p>
        </div>
      </div>

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[600px] expense-breakdown-table">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[800px]">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
            <tr>
              <th scope="col" className="px-3 py-2 font-semibold text-center">День</th>
              <th scope="col" className="px-3 py-2 font-semibold">Дата</th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">Суточные</th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">Проживание</th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">Транспорт</th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">Доп. расходы</th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">Итого за день</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {displayExpenses.map((item, index) => (
              <tr
                key={index}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100"
              >
                <td className="px-3 py-1.5 whitespace-nowrap text-center font-medium">{index + 1}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  {formatDate(item.date, 'date')}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap text-right">{formatCurrency(item.perDiem, 0)}</td>
                <td className="px-3 py-1.5 whitespace-nowrap text-right">
                  {item.accommodation > 0 ? formatCurrency(item.accommodation, 0) : '-'}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap text-right">
                  {item.transport > 0 ? formatCurrency(item.transport, 0) : '-'}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap text-right">
                  {item.additional > 0 ? formatCurrency(item.additional, 0) : '-'}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-gray-800 dark:text-gray-100">
                  {formatCurrency(item.total, 0)}
                </td>
              </tr>
            ))}

            {/* Итоговая строка */}
            <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
              <td className="px-3 py-2 whitespace-nowrap text-center" colSpan="2">Итого:</td>
              <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(totals.perDiem, 0)}</td>
              <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(totals.accommodation, 0)}</td>
              <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(totals.transport, 0)}</td>
              <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(totals.additional, 0)}</td>
              <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(totals.total, 0)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Кнопка показа полной детализации (если она скрыта и больше предпросмотра) */}
      {!showFullBreakdown && expenses.length > 7 && (
        <div className="text-center mt-4">
          <Button
            onClick={() => setShowFullBreakdown(true)}
            icon={<Layers className="w-4 h-4" />}
            variant="link"
          >
            Показать все {expenses.length} дней
          </Button>
        </div>
      )}

      {/* Информация о налогообложении суточных */}
      {results.incomeTax > 0 && (
        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800/40 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-start">
            <DollarSign className="w-5 h-5 text-orange-500 mr-3 mt-1" />
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                Информация о налогообложении суточных
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Согласно НК РФ, суточные облагаются НДФЛ только в части превышения 700 руб. для командировок по России 
                и 2500 руб. для зарубежных командировок. В данном расчете превышение составило 
                {' '}{formatCurrency(results.perDiemExcess)}, НДФЛ с которого составляет {formatCurrency(results.incomeTax)}.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseBreakdown;